// Product JSON-LD of a product page, built by `get_product_JsonLd_server` (`@core/helpers/server/getJsonLds`). Pure: no
// alias imports, so it is unit-tested with node --test. Same approach as jument / chawk-bazar / ciseco
// `helpers/productJsonLd.js`: a product with variants is a `ProductGroup` (`hasVariant` / `variesBy` belong there, not on
// `Product`), the colour label is `color` or `colors` depending on the tenant, and prices follow the site currency
// (`product_meta_currency`, the same table as the `product:*` meta tags: toman x 10 = IRR).
import { find_meta_variant, product_meta_currency } from "./product-meta.js";
import { meta_text } from "./metadata-rules.js";
import { with_trailing_slash } from "../url-path.js";

const VARIANT_DIMENSIONS = [
  { label_keys: ["color", "colors"], property: "color", schema_url: "https://schema.org/color" },
  { label_keys: ["size", "sizes"], property: "size", schema_url: "https://schema.org/size" },
];

/** `origin` + a page path with exactly one `/` between them; an absolute url is kept, the path's trailing slash as given. */
export const json_ld_absolute_url = (origin, path) => {
  if (typeof path !== "string") return undefined;
  const value = path.trim();
  if (!value || value === "#") return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${String(origin || "").replace(/\/+$/, "")}/${value.replace(/^\/+/, "")}`;
};

/** A storage media path under the storage prefix (one `/`); undefined without a path. */
export const json_ld_media_url = (storage_prefix_url, url) => {
  if (typeof url !== "string") return undefined;
  const value = url.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${String(storage_prefix_url || "").replace(/\/+$/, "")}/${value.replace(/^\/+/, "")}`;
};

const gallery_urls = (storage_prefix_url, gallery) =>
  Array.isArray(gallery) ? gallery.map((media) => json_ld_media_url(storage_prefix_url, media?.url)).filter(Boolean) : undefined;

/**
 * The path of a record's page: `seo.full_url`, else `seo.url` when it is a path, else `fallback_path` (the requested
 * path, for the page being rendered). A bare slug in `seo.url` is not a page address, so it is never used.
 */
export const json_ld_page_path = (seo, fallback_path) => {
  const candidates = [seo?.full_url, seo?.url, fallback_path];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const value = candidate.trim();
    if (/^https?:\/\//i.test(value)) return value;
    if (candidate === seo?.full_url && value && value !== "#") return value;
    if (value.startsWith("/") && !value.startsWith("//")) return value;
  }
  return undefined;
};

const label_of = (item, label_keys) => {
  for (const key of label_keys) {
    const label = item?.labels_obj?.[key];
    if (label) return label;
  }
  return undefined;
};

/** Only the dimensions whose title really differs between the variants. */
const varying_dimensions = (mix_variant) =>
  VARIANT_DIMENSIONS.filter(({ label_keys }) => {
    const titles = new Set(mix_variant.map((item) => label_of(item, label_keys)?.title).filter(Boolean));
    return titles.size > 1;
  });

/**
 * @param {object} args
 * @param {object} args.product              `getProductBySlug` data
 * @param {string} [args.origin]             e.g. `https://shop.example.com`
 * @param {string} [args.storage_prefix_url] image CDN prefix
 * @param {string|number} [args.web_currency] site currency setting (1 toman, 2 rial, 3 dollar; default rial)
 * @param {string} [args.request_path]       path of the page being rendered, used when the product has no `seo.full_url`
 * @returns {object} `Product` (no variant or one) or `ProductGroup` (two or more variants)
 */
export const build_product_json_ld = ({ product, origin, storage_prefix_url, web_currency, request_path } = {}) => {
  const { currency, multiplier } = product_meta_currency(web_currency);
  // rial keeps the backend value as it was; toman/dollar convert like the `product:*` meta tags
  const amount = (value) =>
    multiplier === 1 || value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
      ? value
      : Number(value) * multiplier;
  const offer = (item) => ({
    "@type": "Offer",
    priceCurrency: currency,
    price: amount(item?.price?.main_price),
    availability: `https://schema.org/${item?.details?.count > 0 && item?.price?.price > 0 ? "InStock" : "OutOfStock"}`,
  });

  const mix_variant = Array.isArray(product?.mix_variant) ? product.mix_variant.filter(Boolean) : [];
  const is_group = mix_variant.length > 1;

  const obj = {
    "@context": "https://schema.org",
    "@type": is_group ? "ProductGroup" : "Product",
    name: product?.title,
    image: gallery_urls(storage_prefix_url, product?.media_gallery),
    description: product?.description,
    url: json_ld_absolute_url(origin, json_ld_page_path(product?.seo, request_path)),
  };
  if (product?.brand?.title) obj.brand = { "@type": "Brand", name: product.brand.title };

  if (!is_group) {
    // a product without variants, or with only the default one: a plain Product with its own offer
    const source = mix_variant[0] || product;
    if (mix_variant[0]?.details?.product_code) obj.sku = mix_variant[0].details.product_code;
    obj.offers = offer(source);
    return obj;
  }

  const dimensions = varying_dimensions(mix_variant);
  const group_id = product?.id ?? product?.details?.product_code;
  if (group_id !== undefined && group_id !== null && group_id !== "") obj.productGroupID = String(group_id);
  if (dimensions.length > 0) obj.variesBy = dimensions.map(({ schema_url }) => schema_url);
  obj.hasVariant = mix_variant.map((item) => {
    const variant = {
      "@type": "Product",
      sku: item?.details?.product_code,
      image: gallery_urls(storage_prefix_url, item?.media_gallery),
      name: [product?.title, item?.title].filter(Boolean).join(" "),
    };
    for (const { label_keys, property } of VARIANT_DIMENSIONS) {
      const title = label_of(item, label_keys)?.title;
      if (title) variant[property] = title;
    }
    variant.offers = offer(item);
    return variant;
  });
  return obj;
};

// ===== Three-schema product JSON-LD (group / product / selected) =====
// Moved from chawk-bazar / ciseco / jument `helpers/productJsonLd.js` (`build_product_json_lds`, `build_reviews`,
// `absolute_page_url`) so the sites stop keeping their own copies. Behaviour is chawk-bazar's version (the latest one).

// Plain text: no tags, entities decoded, spaces collapsed; empty -> undefined
const clean_text = (value) => meta_text(value) || undefined;

/** sku / product_code without zero-width characters or outer spaces. */
const clean_identifier = (value) => {
  if (typeof value !== "string") return undefined;
  const text = value.replace(/[\u200B-\u200F\uFEFF]/g, "").trim();
  return text || undefined;
};

/** Latin slug of a label key for an `@id` fragment; undefined for a Persian or empty key. */
const slug_part = (value) => {
  if (typeof value !== "string") return undefined;
  const slug = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug || undefined;
};

// ms timestamp (number or digit string; a 10-digit one is seconds) or an ISO string -> ms, else null
const to_ms = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const raw = String(value).trim();
  const number = typeof value === "number" ? value : /^\d+$/.test(raw) ? Number(raw) : Date.parse(raw);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number < 1e11 ? number * 1000 : number;
};

const drop_empty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  );

/**
 * Absolute page url with a trailing `/` (the sites use `trailingSlash: true`). An absolute `path` is kept; a path with
 * a query or hash gets no slash. From chawk-bazar / ciseco / jument `absolute_page_url`.
 */
export const json_ld_page_url = (origin = "", path = "") => {
  if (!path || typeof path !== "string") return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return json_ld_absolute_url(origin, with_trailing_slash(path.startsWith("/") ? path : `/${path}`));
};

/** HTML of the first `texteditor` widget of a product page section (`main` intro, `content` review); "" without one. */
// From chawk-bazar / ciseco `get_widget_html` and jument `find_texteditor`: the product text lives in these widgets.
export const product_widget_html = (widgets, section) => {
  const group = (Array.isArray(widgets) ? widgets : []).find((item) => item?.section === section);
  const widget = (group?.widgets || []).find((item) => item?.widget_component_key === "texteditor");
  const html = widget?.fields?.description;
  return typeof html === "string" ? html : "";
};

/**
 * `Review` items from product comments (from chawk-bazar `build_reviews`). Unconfirmed comments (`confirmed` 0/false)
 * are skipped; a comment without an author name, text or a 1..5 rating is skipped; at most 5.
 */
export const build_product_reviews = (comments = []) =>
  (Array.isArray(comments) ? comments : [])
    .filter((item) => item?.confirmed === undefined || item?.confirmed === null || Boolean(item?.confirmed))
    .map((item) => {
      const author = clean_text(item?.user?.full_name) || clean_text(item?.user?.nickname);
      const rating = Number(item?.user_model_rate);
      const body = clean_text(item?.text);
      if (!author || !body || !Number.isFinite(rating) || rating < 1 || rating > 5) return undefined;
      const ms = to_ms(item?.updatedAt);
      return drop_empty({
        "@type": "Review",
        author: { "@type": "Person", name: author },
        datePublished: ms === null ? undefined : new Date(ms).toISOString().slice(0, 10),
        reviewBody: body,
        reviewRating: { "@type": "Rating", ratingValue: rating, bestRating: 5, worstRating: 1 },
      });
    })
    .filter(Boolean)
    .slice(0, 5);

const list_gallery = (prefix, gallery) => {
  const list = (Array.isArray(gallery) ? gallery : []).map((item) => json_ld_media_url(prefix, item?.url)).filter(Boolean);
  return list.length > 0 ? list : undefined;
};

/** Payable price: `main_price` is the final price (`price` is the struck-through one); 0 means none. */
const payable_price = (price) => {
  const value = Number(price?.main_price);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

const is_purchasable = (details, price) => Number(details?.count) > 0 && payable_price(price) !== undefined;

const build_seller = (seller_name) => (seller_name ? { "@type": "Organization", name: seller_name } : undefined);

// no rating without votes (Google rejects a ratingValue with no count)
const build_aggregate_rating = (product) => {
  const count = Number(product?.rate_counts?.total);
  const value = Number(product?.average_rate);
  if (!Number.isFinite(count) || count < 1) return undefined;
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return { "@type": "AggregateRating", ratingValue: value, reviewCount: count, bestRating: 5, worstRating: 1 };
};

// only priced offers count for low/high/offerCount/availability
const build_aggregate_offer = (offers, { currency, url, seller_name }) => {
  const priced = offers.filter((offer) => Number.isFinite(offer?.price));
  if (priced.length === 0) return undefined;
  const prices = priced.map((offer) => offer.price);
  const in_stock = priced.some((offer) => offer.availability?.endsWith("InStock"));
  const aggregate = {
    "@type": "AggregateOffer",
    priceCurrency: currency,
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
    offerCount: priced.length,
    availability: `https://schema.org/${in_stock ? "InStock" : "OutOfStock"}`,
  };
  if (url) aggregate.url = url;
  const seller = build_seller(seller_name);
  if (seller) aggregate.seller = seller;
  return aggregate;
};

/**
 * The page's product schemas, each meant for its own `<script type="application/ld+json">`:
 * - `group`    `ProductGroup` with every variant in `hasVariant` and an `AggregateOffer`;
 * - `product`  the product itself from its root price / stock / code (`@id` `#product`), with `review`;
 * - `selected` the variant the page shows (`?selected-labels=`), as a full `Product` linked by `inProductGroupWithID`.
 * A product without real variants (fewer than two, or none differing in colour/size) gets only `product`.
 *
 * Prices: `main_price` in the site currency (`web_currency`, toman x 10 = IRR). A variant without a price (the backend
 * sends 0 when out of stock) uses the root price, else the first priced variant; with no price at all there is no Offer.
 *
 * @param {object} args
 * @param {object} args.product              `getProductBySlug` data (needs `seo.full_url`, root `price` / `details`)
 * @param {string} [args.origin]
 * @param {string} [args.storage_prefix_url]
 * @param {string} [args.summary]            intro text/HTML -> `disambiguatingDescription` (or `description` alone)
 * @param {string} [args.description]        long text/HTML -> `description`
 * @param {string|number} [args.web_currency] 1 toman, 2 rial (default), 3 dollar
 * @param {string} [args.seller_name]        `offers.seller` name (the site name); none -> no seller
 * @param {string|string[]} [args.selected_labels] `?selected-labels=` of the page
 * @param {object[]} [args.comments]         first page of comments -> `review` on `product`
 * @param {string} [args.request_path]       page path used when the product has no `seo.full_url` (default: none)
 * @param {number} [args.now]                "now" in ms, for `priceValidUntil` (tests)
 * @returns {{group: object|undefined, product: object|undefined, selected: object|undefined}}
 */
// From chawk-bazar / ciseco / jument `build_product_json_lds` (chawk-bazar's version, the most complete one).
export const build_product_json_lds = ({
  product,
  origin = "",
  storage_prefix_url = "",
  summary,
  description,
  web_currency,
  seller_name,
  selected_labels,
  comments = [],
  request_path,
  now = Date.now(),
} = {}) => {
  if (!product) return { group: undefined, product: undefined, selected: undefined };

  const { currency, multiplier } = product_meta_currency(web_currency);
  const url = json_ld_page_url(origin, json_ld_page_path(product?.seo, request_path));
  const images = list_gallery(storage_prefix_url, product?.media_gallery);
  const title = clean_text(product?.title);
  const mix_variant = Array.isArray(product?.mix_variant) ? product.mix_variant : [];

  // `description` is the main text; the intro goes to `disambiguatingDescription`, or is the description when alone
  const intro_text = clean_text(summary);
  const long_text = clean_text(description);

  const base = drop_empty({
    "@context": "https://schema.org",
    name: title,
    description: long_text || intro_text,
    disambiguatingDescription: long_text ? intro_text : undefined,
    image: images,
    url,
    category: clean_text(product?.category?.title),
    brand: product?.brand?.title ? { "@type": "Brand", name: clean_text(product.brand.title) } : undefined,
    aggregateRating: build_aggregate_rating(product),
  });

  const fallback_price =
    payable_price(product?.price) ?? mix_variant.map((item) => payable_price(item?.price)).find((value) => value !== undefined);
  const variants_in_stock = mix_variant.some((item) => is_purchasable(item?.details, item?.price));

  const build_offer = ({ price, details, stock_fallback }) => {
    const own_price = payable_price(price);
    const value = own_price ?? fallback_price;
    if (value === undefined) return undefined;
    // the root of a variant product has price 0: its stock comes from the variants
    const in_stock =
      own_price === undefined && stock_fallback !== undefined ? stock_fallback : is_purchasable(details, price);
    const offer = {
      "@type": "Offer",
      priceCurrency: currency,
      availability: `https://schema.org/${in_stock ? "InStock" : "OutOfStock"}`,
      itemCondition: "https://schema.org/NewCondition",
    };
    offer.price = value * multiplier;
    if (url) offer.url = url;
    // only a running discount whose end is in the future (a past date is a Rich Results warning)
    const expire_at = parseInt(price?.offer_expireAt);
    if (price?.has_offer && expire_at > now) {
      offer.priceValidUntil = new Date(expire_at).toISOString().slice(0, 10);
    }
    const seller = build_seller(seller_name);
    if (seller) offer.seller = seller;
    return offer;
  };

  const product_node = drop_empty({
    ...base,
    "@type": "Product",
    "@id": url ? `${url}#product` : undefined,
    sku: clean_identifier(product?.details?.product_code) || product?.id,
    offers: build_offer({ price: product?.price, details: product?.details, stock_fallback: variants_in_stock }),
    review: build_product_reviews(comments),
  });

  const label_text = (item, label_keys) => clean_text(label_of(item, label_keys)?.title);
  const dimensions = VARIANT_DIMENSIONS.filter(({ label_keys }) => {
    const titles = new Set(mix_variant.map((item) => label_text(item, label_keys)).filter(Boolean));
    return titles.size > 1;
  });

  // a default `mix_variant` (key "1-1", no labels) is not a group: two variants differing in colour/size are needed
  if (mix_variant.length < 2 || dimensions.length === 0) return { group: undefined, product: product_node, selected: undefined };

  // `@id` fragment from the label keys (`#red-one`); the variant number when there is none or it repeats
  const used_slugs = new Set();
  const variant_fragment = (item, index) => {
    const slug = dimensions
      .map(({ label_keys }) => slug_part(label_of(item, label_keys)?.key))
      .filter(Boolean)
      .join("-");
    if (!slug || used_slugs.has(slug)) return `variant-${index + 1}`;
    used_slugs.add(slug);
    return slug;
  };

  const variants = mix_variant.map((item, index) => {
    const parts = dimensions.map(({ label_keys }) => label_text(item, label_keys)).filter(Boolean);
    const variant = drop_empty({
      "@type": "Product",
      "@id": url ? `${url}#${variant_fragment(item, index)}` : undefined,
      name:
        parts.length === 0
          ? [title, clean_text(item?.title)].filter(Boolean).join(" ")
          : [title, ...parts].filter(Boolean).join(" - "),
      sku: clean_identifier(item?.details?.product_code),
      image: list_gallery(storage_prefix_url, item?.media_gallery) || images,
      offers: build_offer({ price: item?.price, details: item?.details }),
    });
    for (const { label_keys, property } of dimensions) {
      const value = label_text(item, label_keys);
      if (value) variant[property] = value;
    }
    return variant;
  });

  const group = drop_empty({
    ...base,
    "@type": "ProductGroup",
    "@id": url ? `${url}#product-group` : undefined,
    // `id`, not `product_code`: the backend gives every variant the same product_code
    productGroupID: product?.id || clean_identifier(product?.details?.product_code),
    variesBy: dimensions.map(({ schema_url }) => schema_url),
    hasVariant: variants,
    offers: build_aggregate_offer(variants.map((variant) => variant?.offers), { currency, url, seller_name }),
  });

  // the shown variant as its own full Product: a different `@id` and a text link, so it is not merged into the group
  const selected_node = variants[mix_variant.indexOf(find_meta_variant(mix_variant, selected_labels))];
  const selected = selected_node
    ? drop_empty({
        ...base,
        ...selected_node,
        "@id": selected_node["@id"] ? `${selected_node["@id"]}-selected` : undefined,
        inProductGroupWithID: group.productGroupID,
      })
    : undefined;

  return { group, product: product_node, selected };
};
